import { ActionIcon, Button, Collapse, Text, Tooltip } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconPlayerPauseFilled, IconPlayerPlayFilled, IconTrash } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MemoryTranscriptStatus } from 'src/Services/API/Memories.api';
import { trackMemoryRecordingPlayed } from 'src/Services/Mixpanel/events';
import { formatVoiceDuration } from 'src/Utils/memoryDate';

import styles from './VoiceStoryPlayer.module.scss';
import { Props } from './VoiceStoryPlayer.types';

/** One voice story: a play button (a native <audio> stays hidden behind it). */
const VoiceStoryPlayer = ({ recording, onDelete }: Props) => {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [transcriptOpened, setTranscriptOpened] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onEnd = () => setIsPlaying(false);
    audio.addEventListener('ended', onEnd);
    audio.addEventListener('pause', onEnd);
    return () => {
      audio.removeEventListener('ended', onEnd);
      audio.removeEventListener('pause', onEnd);
    };
  }, []);

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      return;
    }
    await audio.play();
    setIsPlaying(true);
    trackMemoryRecordingPlayed({ duration_seconds: recording.durationSeconds });
  };

  const confirmDelete = () =>
    modals.openConfirmModal({
      title: t('memories.recording.deleteTitle', 'Retirer ce récit ?'),
      children: (
        <Text size="sm">
          {t(
            'memories.recording.deleteMessage',
            'La voix de {{name}} et sa transcription ne seront plus dans ce souvenir.',
            { name: recording.speakerDisplayName }
          )}
        </Text>
      ),
      labels: {
        confirm: t('memories.recording.deleteConfirm', 'Retirer'),
        cancel: t('common.cancel', 'Annuler'),
      },
      confirmProps: { color: 'red' },
      onConfirm: () => onDelete(recording.id),
    });

  if (recording.withdrawn) {
    return (
      <div className={styles.container}>
        <div className={styles.details}>
          {t('memories.recording.withdrawn', 'Récit retiré')} — {recording.speakerDisplayName}
        </div>
      </div>
    );
  }

  const playLabel = isPlaying
    ? t('memories.recording.pause', 'Mettre en pause')
    : t('memories.recording.play', 'Écouter');
  const deleteLabel = t('memories.recording.deleteLabel', 'Retirer le récit');

  return (
    <div className={styles.container}>
      <div className={styles.main}>
        <Tooltip label={playLabel} withArrow>
          <ActionIcon
            size="xl"
            radius="xl"
            onClick={togglePlay}
            disabled={!recording.audioUrl}
            aria-label={playLabel}
          >
            {isPlaying ? <IconPlayerPauseFilled size={20} /> : <IconPlayerPlayFilled size={20} />}
          </ActionIcon>
        </Tooltip>
        <div className={styles.meta}>
          <div className={styles.speaker}>{recording.speakerDisplayName}</div>
          <div className={styles.details}>
            {formatVoiceDuration(recording.durationSeconds)} ·{' '}
            {t('memories.recording.recordedOn', 'enregistré le {{date}}', {
              date: dayjs(recording.recordedAt).format('DD/MM/YYYY'),
            })}
          </div>
        </div>
        {recording.canDelete && (
          <Tooltip label={deleteLabel} withArrow>
            <ActionIcon
              variant="outline"
              color="red"
              size="xl"
              radius="xl"
              onClick={confirmDelete}
              aria-label={deleteLabel}
            >
              <IconTrash size={20} />
            </ActionIcon>
          </Tooltip>
        )}
      </div>
      {recording.audioUrl && (
        <audio ref={audioRef} src={recording.audioUrl} preload="none" className={styles.audio} />
      )}

      {recording.transcriptStatus === MemoryTranscriptStatus.Pending && (
        <div className={styles.details}>
          {t('memories.recording.transcribing', 'Transcription en cours…')}
        </div>
      )}
      {recording.transcriptStatus === MemoryTranscriptStatus.Failed && (
        <div className={styles.details}>
          {t('memories.recording.transcriptFailed', 'Transcription indisponible')}
        </div>
      )}
      {recording.transcriptStatus === MemoryTranscriptStatus.Done && recording.transcript && (
        <>
          <Button
            variant="outline"
            className={styles.transcriptToggle}
            onClick={() => setTranscriptOpened(value => !value)}
          >
            {transcriptOpened
              ? t('memories.recording.hideTranscript', 'Masquer le texte')
              : t('memories.recording.showTranscript', 'Lire le texte')}
          </Button>
          <Collapse in={transcriptOpened}>
            <div className={styles.transcript}>{recording.transcript}</div>
          </Collapse>
        </>
      )}
    </div>
  );
};

export default VoiceStoryPlayer;
